import { getExtensionsManager } from '../../extensions/extensions-manager'
import { supportsGlobalSearch } from '../../extensions/utils'

import type {
  GlobalSearchActionUI,
  GlobalSearchCategoryUI,
  GlobalSearchManager,
  GlobalSearchResultPreview,
} from './global-search-models'
import type {
  GlobalSearchAction,
  GlobalSearchCategoryColumn,
  GlobalSearchContext,
  GlobalSearchResult,
  GlobalSearchShellExtension,
  GlobalSearchValueField,
} from '../../extensions'
import { isCommandHandled } from '../../services/namespaces'
import { authenticatedFetch } from '../../services/auth'
import { environment } from '../../environments'
import { handleErrorAndParseJson } from '../../http-utils/utils'

class GlobalSearchManagerImpl implements GlobalSearchManager {
  private categoriesMapInstance: Map<string, GlobalSearchCategoryUI> | undefined = undefined
  private globalSearchExtensions: readonly GlobalSearchShellExtension[] | undefined = undefined

  private getGlobalSearchExtensions = (): readonly GlobalSearchShellExtension[] => {
    if (!this.globalSearchExtensions) {
      const result: GlobalSearchShellExtension[] = []

      getExtensionsManager()
        .getExtensions()
        .forEach(extension => {
          if (supportsGlobalSearch(extension)) {
            result.push(extension)
          }
        })
      this.globalSearchExtensions = result
    }
    return this.globalSearchExtensions
  }

  public getCategories = () => Array.from(this.getCategoriesMap().values())

  public getCategoryById = (id: string) => this.getCategoriesMap().get(id)

  private getCategoryId = (result: GlobalSearchResult) => {
    const extensions = this.getGlobalSearchExtensions()
    for (const extension of extensions) {
      const categoryId = extension.getGlobalSearchResultCategoryId(result)
      if (categoryId) {
        return categoryId
      }
    }
  }

  public getCategory = (result: GlobalSearchResult) => {
    const categoryId = this.getCategoryId(result)
    if (categoryId) {
      return this.getCategoryById(categoryId)
    }
  }

  public getGlobalSearchResultActions = (searchResult: GlobalSearchResult, context: GlobalSearchContext) => {
    const searchResultActionsUI: GlobalSearchActionUI[] = []
    this.getGlobalSearchExtensions().forEach(extension => {
      extension.getGlobalSearchResultActions(searchResult, context)?.forEach(action => {
        if (this.canAddActionTo(action, searchResultActionsUI)) {
          searchResultActionsUI.push(this.convertActionToUI(action, extension))
        }
      })
    })
    return searchResultActionsUI
  }

  private convertActionToUI = (
    { displayNameKey, ...rest }: GlobalSearchAction,
    extension: GlobalSearchShellExtension,
  ) => ({
    ...rest,
    displayName: extension.getString(displayNameKey),
  })

  public getResultDefaultAction = (result: GlobalSearchResult, context: GlobalSearchContext) => {
    const extensions = this.getGlobalSearchExtensions()
    for (const extension of extensions) {
      const defaultAction = extension.getGlobalSearchResultDefaultAction(result, context)
      if (defaultAction) {
        return defaultAction
      }
    }
  }

  public convertToGlobalSearchResultPreview = (result: GlobalSearchResult) => {
    const extensions = this.getGlobalSearchExtensions()

    return extensions.reduce(
      (uiResult: GlobalSearchResultPreview, extension) => {
        const categoryId = extension.getGlobalSearchResultCategoryId(result)

        let defaultActionUI
        if (!uiResult.defaultAction) {
          const defaultAction = extension.getGlobalSearchResultDefaultAction(result, 'preview')
          if (defaultAction) {
            defaultActionUI = this.convertActionToUI(defaultAction, extension)
          }
        }

        if (categoryId) {
          const category = this.getCategoryById(categoryId)
          const title = this.getResultFieldValue(result, category?.titleField) ?? ''
          const preview = this.getResultFieldValue(result, category?.previewField) ?? ''
          const userKey = this.getResultFieldValue(result, category?.userKeyField) ?? ''
          const icon = this.getResultFieldValue(result, category?.iconField) ?? ''

          uiResult = {
            ...uiResult,
            title: uiResult.title || title,
            preview: uiResult.preview || preview,
            userKey: uiResult.userKey !== '' ? uiResult.userKey : userKey,
            icon: uiResult.icon !== '' ? uiResult.icon : icon,
            originalResult: uiResult.originalResult,
            defaultAction: uiResult.defaultAction ?? defaultActionUI,
          }
        }
        const extensionActions = extension.getGlobalSearchResultActions(result, 'preview')

        if (extensionActions) {
          const handledActions: GlobalSearchActionUI[] = []

          extensionActions.forEach(action => {
            if (this.canAddActionTo(action, uiResult.actions)) {
              handledActions.push(this.convertActionToUI(action, extension))
            }
          })
          uiResult = { ...uiResult, actions: [...uiResult.actions, ...handledActions] }
        }
        return uiResult
      },
      {
        title: '',
        preview: '',
        actions: [],
        userKey: '',
        icon: '',
        originalResult: result,
        defaultAction: undefined,
      } as GlobalSearchResultPreview,
    )
  }

  public convertToGlobalSearchResultPreviews = (
    results: readonly GlobalSearchResult[],
  ): readonly GlobalSearchResultPreview[] => results.map(result => this.convertToGlobalSearchResultPreview(result))

  public searchFor = async (
    searchString: string,
    categories?: readonly string[],
  ): Promise<readonly GlobalSearchResult[]> => {
    if (this.getCategories().length) {
      console.log('Search for ', searchString, categories)
      return await this.queryGlobalSearchAPI(encodeURIComponent(searchString))
    }
    return await Promise.resolve([])
  }

  private queryGlobalSearchAPI = async (searchString: string): Promise<GlobalSearchResult[]> => {
    const searchAPIUrl = environment()?.searchBaseUrl + `/search?q=${searchString}`
    const response = authenticatedFetch(searchAPIUrl).then(handleErrorAndParseJson)
    return (await response) as GlobalSearchResult[]
  }

  public getResultFieldValue = (result: GlobalSearchResult, field?: GlobalSearchValueField | undefined) => {
    if (field) {
      if (typeof field === 'string') {
        return result[field] as string
      }
      return field(result)
    }
  }

  private getCategoriesMap = () => {
    if (!this.categoriesMapInstance) {
      const result = new Map<string, GlobalSearchCategoryUI>()
      this.getGlobalSearchExtensions().forEach(extension => {
        extension.getGlobalSearchCategories().forEach(category => {
          const columnsUI = category.columns.map(column => this.convertColumnToUI(column, extension))
          const categoryUI = { ...category, columns: columnsUI }
          result.set(category.id, categoryUI)
        })
      })
      this.categoriesMapInstance = result
    }
    return this.categoriesMapInstance
  }

  private convertColumnToUI = (
    { displayNameKey, ...rest }: GlobalSearchCategoryColumn,
    extension: GlobalSearchShellExtension,
  ) => ({
    ...rest,
    displayName: extension.getString(displayNameKey),
  })

  private canAddActionTo(
    action: GlobalSearchAction,
    actionList: GlobalSearchActionUI[] | readonly GlobalSearchActionUI[],
  ): boolean {
    return isCommandHandled(action.command) && !actionList.find(existingAction => existingAction.id === action.id)
  }
  public getActionsForSearchString = (
    searchString: string,
    context: GlobalSearchContext,
  ): readonly GlobalSearchActionUI[] | undefined => {
    const extensions = this.getGlobalSearchExtensions()
    const result: GlobalSearchActionUI[] = extensions.reduce((actions: GlobalSearchActionUI[], extension) => {
      const extensionActions = extension.getActionsForSearchString(searchString, context)
      if (extensionActions) {
        extensionActions.forEach(action => {
          if (this.canAddActionTo(action, actions)) {
            actions.push(this.convertActionToUI(action, extension))
          }
        })
      }
      return actions
    }, [] as GlobalSearchActionUI[])

    return result
  }
}

const globalSearchManagerInstance = new GlobalSearchManagerImpl()

export const getGlobalSearchManager = (): GlobalSearchManager => globalSearchManagerInstance
